/**
 * 房间设置面板（布局稿 wf-06 / wf-04 右栏）。
 *
 * 当前 Phase 1 后端只有「房间码 + 自报 seat」，没有 host/审批/座位占用接口；
 * 本面板先把真实状态（房间码、在线成员、辩位）呈现出来，并把尚未接通的
 * 房间策略标为 `Phase 1.5`，避免做成看似可用的假开关。
 */
import { useState } from 'react';
import { SIDES, sideLabel, type Side } from '@zongheng/core-schema';
import { useArena } from '../../state/ArenaContext.tsx';
import { Badge, Button } from '../ui/primitives.tsx';

const SEATS = [1, 2, 3, 4] as const;

export function RoomSettingsPanel({ onClose }: { onClose?: () => void }) {
  const { state } = useArena();
  const [copied, setCopied] = useState(false);
  const [approval, setApproval] = useState(false);
  const [locked, setLocked] = useState(false);

  const seatMap = (side: Side) =>
    state.participants.filter(
      (participant) => participant.role === 'DEBATER' && participant.side === side,
    );

  const copyInvite = async () => {
    const code = state.roomId ?? '------';
    const link = `${location.origin}${location.pathname}?room=${code}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="flex h-full flex-col gap-4" data-testid="room-settings">
      <header className="flex items-start justify-between gap-3">
        <div>
          <div className="micro-label">ROOM POLICY</div>
          <h2 className="mt-1 text-[16px] font-semibold text-metal">房间设置</h2>
          <p className="mt-1 text-[11px] text-slate-500">
            房间域与比赛域分离；审批 / 座位锁 / 踢人等在 Phase 1.5 接入。
          </p>
        </div>
        {onClose && (
          <Button
            size="sm"
            variant="ghost"
            onClick={onClose}
            testId="room-settings-close"
            className="whitespace-nowrap shrink-0"
          >
            收起
          </Button>
        )}
      </header>

      <section className="lens-inset p-3">
        <div className="settings-row">
          <span className="text-[11px] text-slate-400">房间码</span>
          <span className="mono text-[13px] font-semibold text-slate-100" data-testid="room-settings-code">
            {state.roomId ?? '未加入'}
          </span>
        </div>
        <div className="settings-row">
          <span className="text-[11px] text-slate-400">当前身份</span>
          <span className="text-[11px] text-slate-200">
            {state.self
              ? `${state.self.name} · ${state.self.role === 'TEACHER' ? '教师' : sideLabel(state.self.side ?? 'AFF')}`
              : '未加入'}
          </span>
        </div>
        <div className="settings-row">
          <span className="text-[11px] text-slate-400">在线 / 容量</span>
          <span className="text-[11px] text-slate-200">
            {state.participants.length} / 12
          </span>
        </div>
        <div className="settings-row">
          <span className="text-[11px] text-slate-400">连接</span>
          <Badge tone={state.connection === 'connected' ? 'pass' : 'warning'}>
            {state.connection}
          </Badge>
        </div>
      </section>

      <section>
        <div className="mb-2 flex items-center justify-between">
          <span className="micro-label">SEATS · (team, seat)</span>
          <span className="text-[10px] text-slate-500">当前为自报辩位</span>
        </div>
        <div className="space-y-3">
          {SIDES.map((side) => {
            const members = seatMap(side);
            return (
              <div key={side}>
                <div className="mb-1.5 flex items-center gap-2">
                  <span
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ background: side === 'AFF' ? '#7dd3fc' : '#fda4af' }}
                  />
                  <span className="text-[11px] font-medium text-slate-300">{sideLabel(side)}</span>
                  <span className="text-[10px] text-slate-500">{members.length}/4</span>
                </div>
                <div className="settings-seat-grid">
                  {SEATS.map((seat) => {
                    const member = members.find((participant) => participant.seat === seat);
                    return (
                      <div
                        key={seat}
                        className="settings-seat"
                        data-filled={Boolean(member)}
                        data-testid={`room-seat-${side}-${seat}`}
                      >
                        <div className="text-[9px] uppercase tracking-wider text-slate-500">
                          {sideLabel(side).slice(0, 1)}
                          {seat}
                        </div>
                        <div className="mt-1 truncate text-[10px] text-slate-200">
                          {member?.name ?? '空位'}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="lens-inset p-3">
        <div className="settings-row">
          <div>
            <div className="text-[11px] text-slate-300">入房审批</div>
            <div className="text-[9px] text-slate-500">Phase 1.5 · 需 host token</div>
          </div>
          <input
            type="checkbox"
            checked={approval}
            disabled
            onChange={(event) => setApproval(event.target.checked)}
            aria-label="入房审批（Phase 1.5）"
          />
        </div>
        <div className="settings-row">
          <div>
            <div className="text-[11px] text-slate-300">锁定座位</div>
            <div className="text-[9px] text-slate-500">Phase 1.5 · 开赛后禁止换位</div>
          </div>
          <input
            type="checkbox"
            checked={locked}
            disabled
            onChange={(event) => setLocked(event.target.checked)}
            aria-label="锁定座位（Phase 1.5）"
          />
        </div>
        <div className="settings-row">
          <div>
            <div className="text-[11px] text-slate-300">媒体策略</div>
            <div className="text-[9px] text-slate-500">
              音频优先 · 教师可监听 · 全员静音
            </div>
          </div>
          <Badge tone="blind">Audio-first</Badge>
        </div>
      </section>

      <div className="mt-auto flex items-center gap-2">
        <Button size="sm" variant="secondary" onClick={() => void copyInvite()} testId="room-settings-copy">
          {copied ? '邀请链接已复制' : '复制邀请链接'}
        </Button>
        <span className="text-[10px] text-slate-500">房间码即加入凭证；PIN 在 Phase 1.5 接入。</span>
      </div>
    </div>
  );
}
